<template>
  <div class="kol-map">
    <div class="kol-map__toolbar">
      <div class="kol-map__search">
        <i class="iconoir-search kol-map__search-icon"></i>
        <input
          v-model="searchQuery"
          type="text"
          class="kol-map__search-input"
          :placeholder="$t('keyoflifeViews.searchPlaceholder')"
        />
        <a
          v-if="searchQuery"
          class="kol-map__search-clear"
          @click="searchQuery = ''"
        >
          <i class="iconoir-cancel"></i>
        </a>
      </div>
      <div class="kol-map__toolbar-right">
        <span class="kol-map__notice">{{
          $t('keyoflifeViews.mapPinsShown', { count: markerCount })
        }}</span>
        <Dropdown
          :value="view.address_field"
          :show-search="false"
          :placeholder="$t('keyoflifeViews.chooseAddressField')"
          class="kol-map__field-dropdown"
          :disabled="readOnly"
          @input="updateField('address_field', $event)"
        >
          <DropdownItem
            :name="$t('keyoflifeViews.noAddress')"
            :value="null"
            icon="iconoir-circle"
          ></DropdownItem>
          <DropdownItem
            v-for="field in textFields"
            :key="field.id"
            :name="field.name"
            :value="field.id"
            icon="iconoir-text"
          ></DropdownItem>
        </Dropdown>
        <Dropdown
          :value="view.latitude_field"
          :show-search="false"
          :placeholder="$t('keyoflifeViews.chooseLatField')"
          class="kol-map__field-dropdown"
          :disabled="readOnly"
          @input="updateField('latitude_field', $event)"
        >
          <DropdownItem
            v-for="field in numberFields"
            :key="field.id"
            :name="field.name"
            :value="field.id"
            icon="iconoir-coordinate"
          ></DropdownItem>
        </Dropdown>
        <Dropdown
          :value="view.longitude_field"
          :show-search="false"
          :placeholder="$t('keyoflifeViews.chooseLngField')"
          class="kol-map__field-dropdown"
          :disabled="readOnly"
          @input="updateField('longitude_field', $event)"
        >
          <DropdownItem
            v-for="field in numberFields"
            :key="field.id"
            :name="field.name"
            :value="field.id"
            icon="iconoir-coordinate"
          ></DropdownItem>
        </Dropdown>
        <Dropdown
          :value="view.color_field"
          :show-search="false"
          :placeholder="$t('keyoflifeViews.chooseColorField')"
          class="kol-map__field-dropdown"
          :disabled="readOnly"
          @input="updateField('color_field', $event)"
        >
          <DropdownItem
            :name="$t('keyoflifeViews.noColor')"
            :value="null"
            icon="iconoir-circle"
          ></DropdownItem>
          <DropdownItem
            v-for="field in singleSelectFields"
            :key="field.id"
            :name="field.name"
            :value="field.id"
            icon="iconoir-fill-color"
          ></DropdownItem>
        </Dropdown>
        <a
          v-if="addressField && latField && lngField && canManageRows"
          class="kol-map__nav-button kol-map__geocode-btn"
          :class="{ 'kol-map__geocode-btn--busy': geocoding }"
          :title="$t('keyoflifeViews.geocodeMissing')"
          @click="geocodeMissing()"
        >
          <i class="iconoir-position"></i>
          <span class="kol-map__geocode-label">{{
            geocoding
              ? geoStatus || $t('keyoflifeViews.geocoding')
              : $t('keyoflifeViews.geocode')
          }}</span>
        </a>
        <a
          class="kol-map__nav-button"
          :title="$t('keyoflifeViews.refresh')"
          @click="fetchRows()"
        >
          <i class="iconoir-refresh-double"></i>
        </a>
      </div>
    </div>

    <div v-if="!latField || !lngField" class="kol-map__empty">
      <i class="iconoir-map-pin kol-map__empty-icon"></i>
      <div class="kol-map__empty-text">
        {{ $t('keyoflifeViews.noCoordFieldsTitle') }}
      </div>
      <div class="kol-map__empty-subtext">
        {{
          numberFields.length >= 2
            ? $t('keyoflifeViews.noCoordFieldsHelp')
            : $t('keyoflifeViews.noNumberFieldsInTable')
        }}
      </div>
      <button
        v-if="canCreateField"
        class="button kol-map__create-fields"
        :class="{ 'button--loading': creatingFields }"
        :disabled="creatingFields"
        @click="createMapFields()"
      >
        {{ $t('keyoflifeViews.createMapFields') }}
      </button>
    </div>

    <div v-else class="kol-map__body">
      <div v-if="loading" class="loading-absolute-center"></div>
      <div ref="mapEl" class="kol-map__canvas"></div>
      <div
        v-if="colorField && legendItems.length"
        class="kol-map__legend"
      >
        <div class="kol-map__legend-title">{{ colorField.name }}</div>
        <div
          v-for="it in legendItems"
          :key="it.id"
          class="kol-map__legend-row"
        >
          <span
            class="kol-map__legend-dot"
            :style="{ background: it.hex }"
          ></span>
          <span class="kol-map__legend-name">{{ it.name }}</span>
        </div>
      </div>
    </div>

    <RowEditModal
      ref="rowEditModal"
      :database="database"
      :table="table"
      :view="view"
      :all-fields-in-table="fields"
      :primary-is-sortable="false"
      :visible-fields="fields"
      :hidden-fields="[]"
      :rows="rows"
      :read-only="!canManageRows"
      :show-hidden-fields="false"
      @update="updateValue"
      @refresh-row="refreshRow"
      @field-updated="$emit('refresh', $event)"
      @field-deleted="$emit('refresh')"
      @field-created="$emit('refresh')"
    ></RowEditModal>
  </div>
</template>

<script>
import 'leaflet/dist/leaflet.css'
import 'leaflet.markercluster/dist/MarkerCluster.css'
import 'leaflet.markercluster/dist/MarkerCluster.Default.css'

import RowEditModal from '@baserow/modules/database/components/row/RowEditModal'
import RowService from '@baserow/modules/database/services/row'
import { notifyIf } from '@baserow/modules/core/utils/error'

import MapService from '@keyoflife-views/services/map'

// Przyblizone hexy nazwanych kolorow Baserow (single-select). Fallback = akcent.
const COLOR_HEX = {
  'light-blue': '#9bcaf6',
  blue: '#5190ef',
  'dark-blue': '#2b6fd6',
  'light-green': '#a0e0b0',
  green: '#37b86b',
  'dark-green': '#1f9b53',
  'light-orange': '#fbcf94',
  orange: '#f5a623',
  'dark-orange': '#e0820a',
  'light-red': '#f7a9a4',
  red: '#e8453c',
  'dark-red': '#c5281f',
  'light-brown': '#d3b79b',
  brown: '#a9714b',
  'light-purple': '#cdaef0',
  purple: '#9b59d0',
  'dark-purple': '#7a3fb0',
  'light-pink': '#f6b6d6',
  pink: '#e85fa8',
  'light-gray': '#d7d8d9',
  gray: '#9aa6ae',
  'dark-gray': '#5b6b78',
}
const ACCENT = '#5190ef'

export default {
  name: 'KeyOfLifeMapView',
  components: { RowEditModal },
  props: {
    fields: { type: Array, required: true },
    view: { type: Object, required: true },
    table: { type: Object, required: true },
    database: { type: Object, required: true },
    readOnly: { type: Boolean, required: true },
    storePrefix: { type: String, required: false, default: '' },
  },
  data() {
    return {
      rows: [],
      totalCount: 0,
      loading: false,
      searchQuery: '',
      fieldOptions: {},
      markerCount: 0,
      creatingFields: false,
      geocoding: false,
      geoStatus: '',
      L: null,
      map: null,
      markerLayer: null,
    }
  },
  computed: {
    addressField() {
      return this.fields.find((f) => f.id === this.view.address_field)
    },
    textFields() {
      return this.fields.filter(
        (f) => f.type === 'text' || f.type === 'long_text'
      )
    },
    latField() {
      return this.fields.find((f) => f.id === this.view.latitude_field)
    },
    lngField() {
      return this.fields.find((f) => f.id === this.view.longitude_field)
    },
    colorField() {
      return this.fields.find((f) => f.id === this.view.color_field)
    },
    numberFields() {
      return this.fields.filter(
        (f) =>
          f.type === 'number' ||
          (f.type === 'formula' && f.formula_type === 'number')
      )
    },
    singleSelectFields() {
      return this.fields.filter((f) => f.type === 'single_select')
    },
    fileFields() {
      return this.fields.filter((f) => f.type === 'file')
    },
    // Legenda: opcje pola koloru z hexami (te same co markery).
    legendItems() {
      if (!this.colorField || !this.colorField.select_options) {
        return []
      }
      return this.colorField.select_options.map((o) => ({
        id: o.id,
        name: o.value,
        hex: COLOR_HEX[o.color] || ACCENT,
      }))
    },
    primaryField() {
      return this.fields.find((f) => f.primary)
    },
    canManageRows() {
      return (
        !this.readOnly &&
        this.$hasPermission(
          'database.table.update_row',
          this.table,
          this.database.workspace.id
        )
      )
    },
    canCreateField() {
      return (
        !this.readOnly &&
        this.$hasPermission(
          'database.table.create_field',
          this.table,
          this.database.workspace.id
        )
      )
    },
    canCreateRow() {
      return (
        !this.readOnly &&
        this.$hasPermission(
          'database.table.create_row',
          this.table,
          this.database.workspace.id
        )
      )
    },
    // Punkty do naniesienia (po filtrze szukajki + poprawne współrzędne).
    points() {
      if (!this.latField || !this.lngField) {
        return []
      }
      const latKey = `field_${this.latField.id}`
      const lngKey = `field_${this.lngField.id}`
      const query = this.searchQuery.trim().toLowerCase()
      const out = []
      for (const row of this.rows) {
        const lat = parseFloat(row[latKey])
        const lng = parseFloat(row[lngKey])
        if (isNaN(lat) || isNaN(lng)) {
          continue
        }
        const title = this.rowTitle(row)
        const extras = this.rowExtras(row)
        if (query) {
          const hay = [title, ...extras.map((e) => e.value)]
            .join(' ')
            .toLowerCase()
          if (!hay.includes(query)) {
            continue
          }
        }
        out.push({ row, lat, lng, title, extras, color: this.rowColor(row) })
      }
      return out
    },
  },
  watch: {
    points() {
      this.renderMarkers()
    },
    'view.id'() {
      this.fetchRows()
    },
  },
  mounted() {
    this.fetchRows().then(() => this.$nextTick(() => this.initMap()))
  },
  beforeUnmount() {
    if (this.map) {
      this.map.remove()
      this.map = null
    }
  },
  methods: {
    async fetchRows() {
      this.loading = true
      try {
        const { data } = await MapService(this.$client).fetchRows({
          viewId: this.view.id,
        })
        this.rows = data.results
        this.totalCount = data.count
        this.fieldOptions = data.field_options || {}
      } catch (error) {
        notifyIf(error, 'rows')
      }
      this.loading = false
    },
    async initMap() {
      if (this.map || !this.$refs.mapEl) {
        return
      }
      // Leaflet wymaga `window` — import wyłącznie po stronie klienta.
      const leaflet = await import('leaflet')
      this.L = leaflet.default || leaflet
      // Klastrowanie markerów (rozszerza obiekt L).
      await import('leaflet.markercluster')
      this.map = this.L.map(this.$refs.mapEl, {
        center: [52.0, 19.0], // Polska — domyślny widok
        zoom: 5,
        scrollWheelZoom: true,
      })
      this.L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        maxZoom: 19,
        attribution: '© OpenStreetMap',
      }).addTo(this.map)
      this.markerLayer = this.L.markerClusterGroup({
        maxClusterRadius: 50,
        showCoverageOnHover: false,
      })
      this.map.addLayer(this.markerLayer)
      // Klik w pustą mapę = nowy wiersz w tym miejscu.
      this.map.on('click', (e) => this.createRowAt(e.latlng))
      this.renderMarkers()
    },
    // Kolorowa pinezka jako divIcon (umożliwia drag, w przeciwieństwie do circleMarker).
    pinIcon(fill) {
      return this.L.divIcon({
        className: 'kol-map__pin-wrap',
        html: `<span class="kol-map__pin" style="background:${fill}"></span>`,
        iconSize: [20, 20],
        iconAnchor: [10, 19],
        popupAnchor: [0, -18],
      })
    },
    renderMarkers() {
      if (!this.map || !this.L || !this.markerLayer) {
        return
      }
      this.markerLayer.clearLayers()
      const latlngs = []
      const draggable = this.canManageRows
      for (const p of this.points) {
        const fill = p.color ? COLOR_HEX[p.color] || ACCENT : ACCENT
        const marker = this.L.marker([p.lat, p.lng], {
          icon: this.pinIcon(fill),
          draggable,
        })
        marker.bindTooltip(p.title || '—', { direction: 'top' })
        marker.bindPopup(this.popupHtml(p), { minWidth: 180, maxWidth: 260 })
        marker.on('popupopen', (e) => {
          const node = e.popup.getElement()
          const btn = node && node.querySelector('.kol-map__popup-open')
          if (btn) {
            btn.onclick = () => this.openRow(p.row)
          }
        })
        if (draggable) {
          marker.on('dragend', (e) => {
            const ll = e.target.getLatLng()
            this.saveCoords(p.row, ll.lat, ll.lng)
          })
        }
        this.markerLayer.addLayer(marker)
        latlngs.push([p.lat, p.lng])
      }
      this.markerCount = latlngs.length
      if (latlngs.length > 0 && !this._fitted) {
        this.map.fitBounds(latlngs, { padding: [40, 40], maxZoom: 15 })
        this._fitted = true
      }
      this.$nextTick(() => this.map && this.map.invalidateSize())
    },
    // Zapis współrzędnych wiersza (po przeciągnięciu markera).
    async saveCoords(row, lat, lng) {
      if (!this.latField || !this.lngField) {
        return
      }
      try {
        const latType = this.$registry.get('field', this.latField.type)
        const lngType = this.$registry.get('field', this.lngField.type)
        const { data } = await RowService(this.$client).update(
          this.table.id,
          row.id,
          {
            [`field_${this.latField.id}`]: latType.prepareValueForUpdate(
              this.latField,
              lat.toFixed(6)
            ),
            [`field_${this.lngField.id}`]: lngType.prepareValueForUpdate(
              this.lngField,
              lng.toFixed(6)
            ),
          }
        )
        this.replaceRowLocally(data)
      } catch (error) {
        notifyIf(error, 'field')
      }
    },
    // Klik w pustą mapę → nowy wiersz z tymi współrzędnymi → panel edycji.
    async createRowAt(latlng) {
      if (!this.canCreateRow || !this.latField || !this.lngField) {
        return
      }
      try {
        const { data } = await RowService(this.$client).create(this.table.id, {
          [`field_${this.latField.id}`]: latlng.lat.toFixed(6),
          [`field_${this.lngField.id}`]: latlng.lng.toFixed(6),
        })
        this.rows.push(data)
        this.totalCount += 1
        this.$nextTick(() => this.$refs.rowEditModal.show(data.id))
      } catch (error) {
        notifyIf(error, 'row')
      }
    },
    async updateField(key, fieldId) {
      if (fieldId === this.view[key]) {
        return
      }
      try {
        await this.$store.dispatch('view/update', {
          view: this.view,
          values: { [key]: fieldId },
        })
      } catch (error) {
        notifyIf(error, 'view')
      }
    },
    // Tworzy pola mapy (Adres tekst + Lat/Lng number) i podpina do widoku.
    async createMapFields() {
      if (this.creatingFields) {
        return
      }
      this.creatingFields = true
      try {
        const mk = async (type, name, values = {}) => {
          const { newField } = await this.$store.dispatch('field/create', {
            type,
            table: this.table,
            values: { name, ...values },
          })
          return newField
        }
        const addr = await mk('text', this.$t('keyoflifeViews.fieldAddress'))
        const lat = await mk('number', this.$t('keyoflifeViews.fieldLat'), {
          number_decimal_places: 6,
        })
        const lng = await mk('number', this.$t('keyoflifeViews.fieldLng'), {
          number_decimal_places: 6,
        })
        this.$emit('refresh')
        await this.$store.dispatch('view/update', {
          view: this.view,
          values: {
            address_field: addr.id,
            latitude_field: lat.id,
            longitude_field: lng.id,
          },
        })
        await this.fetchRows()
        this.$nextTick(() => this.initMap())
      } catch (error) {
        notifyIf(error, 'field')
      }
      this.creatingFields = false
    },
    // Geokoduje brakujące adresy partiami (backend throttluje 1 req/s).
    // Pętla aż batch nic nie zgeokoduje (brak postępu = stop) lub remaining=0.
    async geocodeMissing() {
      if (this.geocoding) {
        return
      }
      this.geocoding = true
      this.geoStatus = ''
      let totalGeocoded = 0
      let totalFailed = 0
      try {
        for (let guard = 0; guard < 100; guard++) {
          const { data } = await MapService(this.$client).geocode({
            viewId: this.view.id,
            batch: 5,
          })
          totalGeocoded += data.geocoded || 0
          totalFailed += data.failed || 0
          this.geoStatus = `${totalGeocoded}✓ ${totalFailed}✗`
          await this.fetchRows() // markery rosną na bieżąco
          if ((data.remaining || 0) <= 0 || (data.geocoded || 0) === 0) {
            break
          }
        }
      } catch (error) {
        notifyIf(error, 'rows')
      }
      this.geoStatus = ''
      this.geocoding = false
    },
    openRow(row) {
      this.$refs.rowEditModal.show(row.id)
    },
    async updateValue({ row, field, value }) {
      try {
        const fieldType = this.$registry.get('field', field.type)
        const prepared = fieldType.prepareValueForUpdate(field, value)
        const { data } = await RowService(this.$client).update(
          this.table.id,
          row.id,
          { [`field_${field.id}`]: prepared }
        )
        this.replaceRowLocally(data)
      } catch (error) {
        notifyIf(error, 'field')
      }
    },
    replaceRowLocally(updated) {
      const index = this.rows.findIndex(
        (r) => r !== null && r.id === updated.id
      )
      if (index !== -1) {
        this.rows.splice(index, 1, updated)
      } else {
        this.rows.push(updated)
      }
    },
    async refreshRow(row) {
      try {
        const { data } = await RowService(this.$client).get(
          this.table.id,
          row.id
        )
        this.replaceRowLocally(data)
      } catch (error) {
        notifyIf(error, 'row')
      }
    },
    rowTitle(row) {
      const field = this.primaryField
      if (!field) {
        return ''
      }
      const fieldType = this.$registry.get('field', field.type)
      const value = fieldType.toHumanReadableString(
        field,
        row[`field_${field.id}`]
      )
      return value || this.$t('keyoflifeViews.unnamed')
    },
    rowExtras(row) {
      const excluded = new Set(
        [
          this.primaryField && this.primaryField.id,
          this.view.address_field,
          this.view.latitude_field,
          this.view.longitude_field,
          this.view.color_field,
        ].filter((id) => id != null)
      )
      const extras = []
      for (const field of this.fields) {
        if (excluded.has(field.id) || extras.length >= 2) {
          continue
        }
        const fieldType = this.$registry.get('field', field.type)
        const text = fieldType.toHumanReadableString(
          field,
          row[`field_${field.id}`]
        )
        if (text !== null && text !== undefined && String(text).trim() !== '') {
          extras.push({ fieldId: field.id, value: String(text) })
        }
      }
      return extras
    },
    rowColor(row) {
      if (!this.colorField) {
        return null
      }
      const value = row[`field_${this.colorField.id}`]
      return value && value.color ? value.color : null
    },
    // URL miniatury pierwszego pola plikowego z obrazem (lub null).
    rowImageUrl(row) {
      for (const field of this.fileFields) {
        const value = row[`field_${field.id}`]
        if (Array.isArray(value) && value.length > 0) {
          const f = value[0]
          if (f.is_image || (f.mime_type && f.mime_type.startsWith('image/'))) {
            const thumb = f.thumbnails && f.thumbnails.small
            return (thumb && thumb.url) || f.url || null
          }
        }
      }
      return null
    },
    escapeHtml(str) {
      return String(str).replace(
        /[&<>"']/g,
        (c) =>
          ({
            '&': '&amp;',
            '<': '&lt;',
            '>': '&gt;',
            '"': '&quot;',
            "'": '&#39;',
          }[c])
      )
    },
    // Treść popupu markera: tytuł + miniatura + dodatkowe pola + przycisk.
    popupHtml(p) {
      const parts = []
      parts.push(
        `<div class="kol-map__popup-title">${this.escapeHtml(p.title)}</div>`
      )
      const img = this.rowImageUrl(p.row)
      if (img) {
        parts.push(
          `<img class="kol-map__popup-img" src="${this.escapeHtml(
            img
          )}" alt="" />`
        )
      }
      for (const extra of p.extras) {
        parts.push(
          `<div class="kol-map__popup-field">${this.escapeHtml(
            extra.value
          )}</div>`
        )
      }
      parts.push(
        `<button class="kol-map__popup-open">${this.escapeHtml(
          this.$t('keyoflifeViews.openRow')
        )}</button>`
      )
      return `<div class="kol-map__popup">${parts.join('')}</div>`
    },
  },
}
</script>
